import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Send, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const CONTACT_RECEIVER_EMAIL = process.env.REACT_APP_CONTACT_RECEIVER_EMAIL;

export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    firm_name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB limit');
        return;
      }
      setFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
        throw new Error('Missing EmailJS environment configuration');
      }

      if (!CONTACT_RECEIVER_EMAIL) {
        throw new Error('Missing recipient email configuration');
      }

      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      setIsSuccess(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      
      // Reset form
      setFormData({
        name: '',
        firm_name: '',
        email: '',
        phone: '',
        message: '',
      });
      setFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Reset success state after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      const details = error?.text || error?.message || 'Unknown error';
      toast.error(`Failed to send message. ${details}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(10,25,47,0.08) 0%, #f5f5f0 50%, rgba(217,48,37,0.1) 100%)'
      }}
      data-testid="contact-section"
    >
      {/* Gradient orbs */}
      <div className="absolute top-20 left-10 w-80 h-80 bg-[#0a192f]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-64 h-64 bg-[#D93025]/8 rounded-full blur-3xl" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
                Get In Touch
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
                Let's Discuss Your Needs
              </h2>
              <p className="text-base md:text-lg text-[#64748b]">
                Ready to streamline your legal operations? Reach out to our team for a 
                confidential consultation about how we can support your practice.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              {/* Contact details hidden */}
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-xl border border-[#e2e2dc] p-6">
              <h3 className="font-heading font-medium text-[#0a192f] mb-4">Business Hours</h3>
              <div className="space-y-2 text-sm text-[#64748b]">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="font-medium text-[#0a192f]">9:00 AM - 6:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="font-medium text-[#0a192f]">By Appointment</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="font-medium text-[#0a192f]">Closed</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-2xl border border-[#e2e2dc] p-8 shadow-sm">
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <input type="hidden" name="to_email" value={CONTACT_RECEIVER_EMAIL || ''} />
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-[#0a192f]">
                      Name <span className="text-[#D93025]">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      required
                      className="rounded-xl border-[#e2e2dc] focus:border-[#0a192f] focus:ring-[#0a192f]/10"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="firm_name" className="text-sm font-medium text-[#0a192f]">
                      Firm Name
                    </Label>
                    <Input
                      id="firm_name"
                      name="firm_name"
                      value={formData.firm_name}
                      onChange={handleInputChange}
                      placeholder="Smith & Associates"
                      className="rounded-xl border-[#e2e2dc] focus:border-[#0a192f] focus:ring-[#0a192f]/10"
                      data-testid="contact-firm-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-[#0a192f]">
                      Email <span className="text-[#D93025]">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@lawfirm.com"
                      required
                      className="rounded-xl border-[#e2e2dc] focus:border-[#0a192f] focus:ring-[#0a192f]/10"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-[#0a192f]">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="rounded-xl border-[#e2e2dc] focus:border-[#0a192f] focus:ring-[#0a192f]/10"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-[#0a192f]">
                  Message <span className="text-[#D93025]">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your legal support needs..."
                  required
                  rows={5}
                  className="rounded-xl border-[#e2e2dc] focus:border-[#0a192f] focus:ring-[#0a192f]/10 resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#0a192f]">
                  Attachment (Optional)
                </Label>
                <div className="relative">
                  <input
                    ref={fileInputRef}
                    name="attachment"
                    type="file"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    accept=".pdf,.doc,.docx,.txt"
                    data-testid="contact-file-input"
                  />
                  <div className="flex items-center gap-3 px-4 py-3 border border-dashed border-[#e2e2dc] rounded-xl hover:border-[#0a192f]/50 transition-colors bg-[#f5f5f0]/50">
                    <Upload className="h-5 w-5 text-[#64748b]" />
                    <span className="text-sm text-[#64748b]">
                      {fileName || 'Click to upload a file (max 5MB)'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-[#64748b]">
                  Supported formats: PDF, DOC, DOCX, TXT
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`w-full rounded-xl px-8 py-4 font-medium transition-all text-base ${
                  isSuccess
                    ? 'bg-green-600 hover:bg-green-600'
                    : 'bg-[#0a192f] hover:bg-[#0a192f]/90 shadow-lg shadow-[#0a192f]/20'
                }`}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : isSuccess ? (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Message Sent!
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
    </section>
  );
};
